import { LDadd, LDdelete, LDinsertResource, LDdeleteResource, LDreplaceResource, LDreplace, Literal, LDupdate, LDupdateList } from './ld-patch-model';

/*
Notes:
Support JSON-LD 1.0
We do not support @container: "@list" for strings at the moment
We do not support updates to language literals where language has changed

Potential support for JSON-LD 1.1
We do not support @container: ["@set", "@language"] but would be nice as an enhancement
*/

/**
 * 
 * @param {*} getLoadedChannels Function to retrieve loaded channel information
 * @param {*} getBoundObjects Function to retrieve bound object information
 * @param {*} internalContext The JSON-LD context used by the system
 * @param {*} patcher A reference to a parse instance
 * @param {*} framesManager A reference to the JSON-LD frames manager
 * @param {*} resourceProcessor Function to process new data
 */
function LdPatchProcessor(getLoadedChannels, getBoundObjects, internalContext, patcher, framesManager, resourceProcessor) {

	const integerXSDtype = "http://www.w3.org/2001/XMLSchema#integer";
	const booleanXSDtype = "http://www.w3.org/2001/XMLSchema#boolean";
	const decimalXSDtype = "http://www.w3.org/2001/XMLSchema#decimal";

	// We use a JISON parser to parse the LD Patch syntax locally and execute accordingly
	// Currently we're only supporting string literal with no language or datatype bits

	let sessionID = "";

	// If a resource has only been partially loaded we don't want to update the data so we ignore not loaded predicates
	function checkIfPredicateLoaded(boNode, predicate) {
		// If a full load loadOptions will be {}
		if (Object.keys(boNode._loadOptions).length === 0) return true;
		const loaded = boNode._loadOptions.hasOwnProperty('predicates') ? boNode._loadOptions.predicates : null;
		if (loaded !== null) {
			if (loaded.length > 0 && loaded.includes(predicate)) return true;
			return false;
		}
		return true;
	}

	function getUpdateProperty(value, graphIRI) {
		// If there is no alias we compute the equivalent information from the ontology data for the resource
		const propertyAlias = internalContext._context.byIRI[value];
		// We return only if an alias. If not an alias the definition may vary between graphs
		// So we recompute on the fly each time
		if (propertyAlias) return propertyAlias;
		if (!graphIRI) throw new Error("For an unliased property a property graph must be supplied. " + value + " " + graphIRI);
		// If an IRI that isn't listed in the context index add it
		if (value.startsWith("https://") || value.startsWith("http://")) {
			internalContext._context.byProperty[value] = value;
			internalContext._context.byIRI[value] = value;
			if (internalContext._context.propertyType[value]) delete internalContext._context.propertyType[value];
			// We don't record the datatype because JSON-LD serialisation on server won't know about it so we 
			// use default JSON-LD approach of @value, @id and @type props
			return value;
		}
	}

	/**
	 * Where a resource is replaced in a ReplaceResource operation there may be local triples pointing at that resource
	 * We need to remove these because they may not be relevant to the replacing resource
	 * Any operation that generated one of these triples will be transformed against the ReplaceResource operation
	 * in to a No-op on the bridge essentially making it disappear
	 * @param {} replacedIri The IRI being replaced
	 */
	function removeTriplesObjects(replacedIri) {
		const context = framesManager.getFrame();
		// We iterate over every loaded resource
		Object.keys(getLoadedChannels()).forEach(id => {
			const data = getLoadedChannels()[id];
			// For all properties of a loaded resource
			Object.keys(data)
				// Filter for properties that are object properties
				.filter(property => context["@context"][property] && context["@context"][property]["@type"] === "@id")
				.forEach(property => {
					// For each property remove any instances where the value if the replaced IRI
					if (Array.isArray(data[property])) {
						let exists = data[property].indexOf(replacedIri);
						if (exists > -1) {
							data[property].splice(exists, 1);
							const boNode = getBoundObjects()[id];
							// Notify UI we have done the removal
							processChangeEvents(boNode, "DELETE", { "property": property });
						}
					}
					else if (data[property] === replacedIri) {
						delete data[property];
						const boNode = getBoundObjects()[id];
						// Notify UI we have done the removal
						processChangeEvents(boNode, "DELETE", { "property": property });
					}
				});
		});
	}

	function processReplaceResource(ldrr, message) {
		const iri = ldrr.getIRI().getText();
		const graphIRI = ldrr.getGraph().getText();
		const id = graphIRI + "___" + iri;
		const boExistingNode = getBoundObjects()[id];
		processChangeEvents(boExistingNode, "REPLACERESOURCE", { iri: iri });
		// Remove all trace of the local resource being replaced
		// Do we also need to cleanup pending revisions and the bridge here?
		// There is a tiny chance that for a non-empty replacement edits are in pending/bridge for resource being replaced
		delete getLoadedChannels()[id];
		delete getBoundObjects()[id];
		// Now clean up any local triples pointing at replaced resource as they are probably not relevant
		removeTriplesObjects(id);
		// If relevant put in place the replacing resource
		if (!ldrr.getEmptyReplacement()) {
			const replacementIri = ldrr.getReplacementIRI().getText();
			const replacementId = graphIRI + "___" + replacementIri;
			// Add in the replacing resource
			resourceProcessor(message.jsonld, graphIRI);
			const boNode = getBoundObjects()[replacementId];
			processChangeEvents(boNode, "REPLACERESOURCE", { iri: replacementIri });
		}
	}

	function processInsertResource(ldir, message) {
		const iri = ldir.getIRI().getText();
		const graphIRI = ldir.getGraph().getText();
		const id = graphIRI + "___" + iri;
		resourceProcessor(message.jsonld, graphIRI);
		const boNode = getBoundObjects()[id];
		processChangeEvents(boNode, "INSERTRESOURCE", { iri: iri });
	}

	function processDeleteResource(lddr) {
		const iri = lddr.getIRI().getText();
		const graphIRI = lddr.getGraph().getText();
		const id = graphIRI + "___" + iri;
		const updateNode = getLoadedChannels()[id];
		if (!updateNode) throw new Error("Attempting to delete an unloaded resource: " + iri);
		const boNode = getBoundObjects()[id];
		// This will stop any further local edits
		updateNode["pendingDeletion"] = true;
		processChangeEvents(boNode, "DELETERESOURCE", { iri: iri });
	}

	function processAdd(lda, isEntailed = false) {

		const iri = lda.getSubject().getText();
		const predicateIRI = lda.getPredicate().getText();
		const graphIRI = lda.getGraph().getText();
		const id = graphIRI + "___" + iri;
		const updateNode = getLoadedChannels()[id];
		if (!updateNode) throw new Error("Attempting to update an unloaded resource: " + lda.getSubject().getText());
		const boNode = getBoundObjects()[id];
		const isLoaded = checkIfPredicateLoaded(boNode, predicateIRI);
		if (!isLoaded) return;

		const updateProperty = getUpdateProperty(predicateIRI, graphIRI);
		const containerType = internalContext._context.propertyContainer[updateProperty];
		let text = lda.getObject().getText();
		// For aliased types get the alias as that it what will be in the JSON
		if (updateProperty === "type" && internalContext._context.byIRI[text]) {
			text = internalContext._context.byIRI[text];
		}

		if (lda.getObject().constructor === Literal) {
			// Container should not be @list
			const updateThing = calcLiteral(lda.getObject(), updateProperty);
			if (containerType === '@list') throw new Error("Literal lists not supported.");
			if (containerType === '@set') {
				// First we need to check if property exists ...
				if (updateNode[updateProperty] == null) {
					updateNode[updateProperty] = [];
				}
				updateNode[updateProperty].push(updateThing);
				if (isEntailed) {
					const entDetail = {
						[updateProperty]: updateNode[updateProperty].length
					};
					updateNode._entailed ? updateNode._entailed.push(entDetail) : updateNode._entailed = [entDetail];
				}
			}
			else if (containerType === '@language') {
				// First we need to check if property exists ...
				const lang = lda.getObject().getLanguage();
				if (updateNode[updateProperty] == null) {
					updateNode[updateProperty] = {};
				}
				// Does language exist?
				if (updateNode[updateProperty][lang]) {
					// If not an array we need to convert to an array because now multiple values
					if (!Array.isArray(updateNode[updateProperty][lang])) {
						updateNode[updateProperty][lang] = [updateNode[updateProperty][lang]];
					}
					updateNode[updateProperty][lang].push(text);
					if (isEntailed) {
						if (!updateNode._entailed) updateNode._entailed = {};
						if (!updateNode._entailed[updateProperty]) updateNode._entailed[updateProperty] = {};
						if (!updateNode._entailed[updateProperty][lang]) updateNode._entailed[updateProperty][lang] = [];
						updateNode._entailed[updateProperty][lang].push(text);
					}
				}
				else {
					updateNode[updateProperty][lang] = text;
					if (isEntailed) {
						if (!updateNode._entailed) updateNode._entailed = {};
						if (!updateNode._entailed[updateProperty]) updateNode._entailed[updateProperty] = {};
						if (!updateNode._entailed[updateProperty][lang]) updateNode._entailed[updateProperty][lang] = [];
						updateNode._entailed[updateProperty][lang].push(text);
					}
				}
			}
			else {
				if (Array.isArray(updateNode[updateProperty])) {
					updateNode[updateProperty].push(updateThing);
				}
				else {
					if (updateNode[updateProperty]) {
						updateNode[updateProperty] = [
							updateNode[updateProperty]
						];
						updateNode[updateProperty].push(updateThing);
					}
					else updateNode[updateProperty] = updateThing;
				}
				if (isEntailed)
					updateNode._entailed ? updateNode._entailed.push(updateProperty) : updateNode._entailed = [updateProperty];
			}
			processChangeEvents(boNode, "ADD", { "property": updateProperty, "entailment": isEntailed });
		} else {
			if (containerType === '@set') {
				// First we need to check if property exists ...
				if (updateNode[updateProperty] == null) {
					updateNode[updateProperty] = [];
				}
				const updateThing = calcIRI(lda.getObject(), boNode, updateProperty);
				updateNode[updateProperty].push(updateThing);
				if (isEntailed) {
					// We maintain the array indices of entailed values
					const entDetail = {
						[updateProperty]: updateNode[updateProperty].length
					};
					updateNode._entailed ? updateNode._entailed.push(entDetail) : updateNode._entailed = [entDetail];
				}
			}
			// We don't handle entailment for lists at the moment
			else if (containerType === '@list') {
				// First we need to check if property exists ... it would be an error if it did
				if (updateNode[updateProperty]) throw new Error("Trying to add a list that already exists");
				// The only possible allowed value is adding rdf:nil
				if (text !== "http://www.w3.org/1999/02/22-rdf-syntax-ns#nil") throw new Error("Trying to add a list with a value that is not rdf:nil");
				// If all okay all we need is an empty array which is how an empty list will serialise in JSON-LD
				updateNode[updateProperty] = [];
			}
			else {
				const updateThing = calcIRI(lda.getObject(), boNode, updateProperty);
				if (Array.isArray(updateNode[updateProperty])) {
					updateNode[updateProperty].push(updateThing);
				}
				else {
					if (updateNode[updateProperty]) {
						updateNode[updateProperty] = [
							updateNode[updateProperty]
						];
						updateNode[updateProperty].push(updateThing);
					}
					else updateNode[updateProperty] = updateThing;
				}
				if (isEntailed)
					updateNode._entailed ? updateNode._entailed.push(updateProperty) : updateNode._entailed = [updateProperty];
			}
			processChangeEvents(boNode, "ADD", { "property": updateProperty, "entailment": isEntailed });
		}
	}

	function processDelete(ldd) {

		const iri = ldd.getSubject().getText();
		const predicateIRI = ldd.getPredicate().getText();
		const graphIRI = ldd.getGraph().getText();
		const id = graphIRI + "___" + iri;
		const updateNode = getLoadedChannels()[id];
		if (!updateNode) throw new Error("Attempting to update an unloaded resource: " + ldd.getSubject().getText());
		const boNode = getBoundObjects()[id];
		const isLoaded = checkIfPredicateLoaded(boNode, ldd.getPredicate().getText());
		if (!isLoaded) return;

		let key = null;
		const updateProperty = getUpdateProperty(predicateIRI, graphIRI);
		const containerType = internalContext._context.propertyContainer[updateProperty];
		const propertyType = internalContext._context.propertyType[updateProperty];
		let text = ldd.getObject().getText();
		// For aliased types get the alias as that it what will be in the JSON
		if (updateProperty === "type" && internalContext._context.byIRI[text]) {
			text = internalContext._context.byIRI[text];
		}

		if (ldd.getObject().constructor === Literal) {
			const lang = ldd.getObject().getLanguage();
			const datatype = ldd.getObject().getDatatype();
			if (containerType === '@set' || Array.isArray(updateNode[updateProperty])) {
				for (let i = 0; i < updateNode[updateProperty].length; i++) {
					let un = updateNode[updateProperty][i];
					let valueProp = internalContext._context.byIRI['@value'];
					let languageProp = internalContext._context.byIRI['@language'];
					let datatypeProp = internalContext._context.byIRI['@type'];
					// Check not overridden locally
					let valueText = null;
					let languageText = null;
					let datatypeText = null;
					if (un['@value']) {
						valueText = un['@value'];
					} else if (un[valueProp]) {
						valueText = un[valueProp];
					} else {
						valueText = un;
					}
					if (un['@language']) {
						languageText = un['@language'];
					} else if (un[languageProp]) {
						languageText = un[languageProp];
					}
					if (un[datatypeProp]) {
						datatypeText = un[datatypeProp];
					} else if (typeof un === "boolean") {
						datatypeText = booleanXSDtype;
						valueText = valueText ? "true" : "false";
					}
					// If it looks likes a number and the existing value is compatible or the datatypes match up map
					// Not sure this is needed because non integers always carry datatype
					else if ((typeof un === "number" || (datatype === propertyType))
						&& (datatype === integerXSDtype || datatype === decimalXSDtype)) {
						// Check for decimal
						if (un.toString().indexOf(".") > 0) {
							datatypeText = decimalXSDtype;
						}
						else datatypeText = integerXSDtype;
					}
					// Note we need == here because of the issue of numbers as numbers or strings
					// The datatype checking handle the rest of the check
					if (text == valueText && lang === languageText && datatype === datatypeText) {
						key = i;
						break;
					}
				}
				if (key !== null) {
					updateNode[updateProperty].splice(key, 1);
					processChangeEvents(boNode, "DELETE", { "property": updateProperty });
				}
				else throw new Error("Cannot compute literal to remove from set");
			}
			else if (containerType === '@language') {
				if (Array.isArray(updateNode[updateProperty][lang])) {
					const langItemIndex = updateNode[updateProperty][lang].indexOf(text);
					updateNode[updateProperty][lang].splice(langItemIndex, 1);
				}
				else {
					delete updateNode[updateProperty][lang];
				}
				processChangeEvents(boNode, "DELETE", { "property": updateProperty });
			} else {
				// Assuming only one property with name p and that the value is correct!!!
				delete updateNode[updateProperty];
				processChangeEvents(boNode, "DELETE", { "property": updateProperty });
			}
		} else {
			if (containerType === '@set' || Array.isArray(updateNode[updateProperty])) {
				const idProp = internalContext._context.byIRI["@id"];
				if (updateNode[updateProperty]) {
					for (let i = 0; i < updateNode[updateProperty].length; i++) {
						const item = updateNode[updateProperty][i];
						// Array item is just an IRI
						if (typeof item === "string") {
							if (item === text) {
								key = i;
								break;
							}
						}
						// The @id or aliased @id object form
						else if (item[idProp] === text) {
							key = i;
							break;
						}
					}
				}
				if (key !== null) {
					updateNode[updateProperty].splice(key, 1);
					processChangeEvents(boNode, "DELETE", { "property": updateProperty });
				}
				else throw new Error("Cannot compute IRI to remove from set");
			} else {
				// Assuming only one property with name p and that the value is correct!!!
				delete updateNode[updateProperty];
				processChangeEvents(boNode, "DELETE", { "property": updateProperty });
			}
		}
	}

	function processUpdate(ldu) {

		const iri = ldu.getSubject().getText();
		const predicateIRI = ldu.getPredicate().getText();
		const graphIRI = ldu.getGraph().getText();
		const id = graphIRI + "___" + iri;
		const updateNode = getLoadedChannels()[id];
		if (!updateNode) throw new Error("Attempting to update an unloaded resource: " + graphIRI + " " + iri);
		const boNode = getBoundObjects()[id];
		const isLoaded = checkIfPredicateLoaded(boNode, ldu.getPredicate().getText());
		if (!isLoaded) return;

		const updateProperty = getUpdateProperty(predicateIRI, graphIRI);
		const containerType = internalContext._context.propertyContainer[updateProperty];
		const valueProp = internalContext._context.byIRI['@value'];
		const languageProp = internalContext._context.byIRI['@language'];
		const datatypeProp = internalContext._context.byIRI['@type'];
		const language = internalContext._context.propertyLanguage[updateProperty];

		if (ldu.getObject().constructor === Literal) {
			const datatype = ldu.getObject().getDatatype();
			const replacementType = ldu.getObjectReplacement().getDatatype();
			if (containerType === '@set' || Array.isArray(updateNode[updateProperty])) {
				// Ensure property exists if not already there
				if (updateNode[updateProperty] == null) {
					updateNode[updateProperty] = [];
					processChangeEvents(updateNode, "ADD-PROPERTY", {
						"property": updateProperty
					});
				}
				// Need to find the existing entry if it exists
				const text = ldu.getObject().getText();
				const lang = ldu.getObject().getLanguage();

				let key = null;
				for (let i = 0; i < updateNode[updateProperty].length; i++) {
					const un = updateNode[updateProperty][i];
					// Check not overridden locally
					let existingValueText = null;
					let existingLanguageText = null;
					let existingDatatypeText = null;
					if (un['@value']) {
						existingValueText = un['@value'];
					} else if (un[valueProp]) {
						existingValueText = un[valueProp];
					} else {
						existingValueText = un;
					}
					if (un['@language']) {
						existingLanguageText = un['@language'];
					} else if (un[languageProp]) {
						existingLanguageText = un[languageProp];
					}
					if (un['@type']) {
						existingDatatypeText = un['@type'];
					}
					else if (un[datatypeProp]) {
						existingDatatypeText = un[datatypeProp];
					}
					else if (typeof un === "boolean") {
						existingDatatypeText = booleanXSDtype;
					}
					// Where a patch includes the string datatype we also need to match string with no datatype
					else if (typeof un === "string" && datatype == "http://www.w3.org/2001/XMLSchema#string") {
						existingDatatypeText = "http://www.w3.org/2001/XMLSchema#string";
					}
					// If it looks likes a number and the existing value is compatible map
					else if (typeof un === "number" && (datatype === integerXSDtype || datatype === decimalXSDtype)) {
						// Check for decimal
						if (un.toString().indexOf(".") > 0) {
							existingDatatypeText = decimalXSDtype;
						}
						else existingDatatypeText = integerXSDtype;
					}
					// The datatype checking handle the rest of the check
					if (text === existingValueText.toString() && lang === existingLanguageText && datatype === existingDatatypeText) {
						key = i;
						break;
					}
				}
				if (key !== null) {
					// Update @value form
					if (updateNode[updateProperty][key]["@value"]) {
						updateNode[updateProperty][key]["@value"] = ldu.getObjectReplacement().getText();
						if (replacementType !== null) {
							updateNode[updateProperty][key][datatypeProp] = replacementType;
						}
						else {
							// If no replacing type make sure we delete any existing type
							if (updateNode[updateProperty][key][datatypeProp]) delete updateNode[updateProperty][key][datatypeProp];
						}
						if (language) {
							updateNode[updateProperty][key][languageProp] = ldu.getObjectReplacement().getLanguage();
						}
					} else if (valueProp !== 'undefined' && updateNode[updateProperty][key][valueProp]) {
						// Updating form where @value is aliased
						updateNode[updateProperty][key][valueProp] = ldu.getObjectReplacement().getText();
						if (replacementType !== null) {
							updateNode[updateProperty][key][datatypeProp] = replacementType;
						}
						else {
							// If no replacing type make sure we delete any existing type
							if (updateNode[updateProperty][key][datatypeProp]) delete updateNode[updateProperty][key][datatypeProp];
						}
						if (language) {
							updateNode[updateProperty][key][languageProp] = ldu.getObjectReplacement().getLanguage();
						}
					} else {
						let replacementText = ldu.getObjectReplacement().getText();
						if (datatype === integerXSDtype || datatype === decimalXSDtype) {
							replacementText = Number(replacementText);
						}
						// Not sure we should ever get an array of booleans!
						else if (datatype === booleanXSDtype) {
							replacementText = replacementText === "true";
						}
						updateNode[updateProperty][key] = replacementText;
					}
					processChangeEvents(boNode, "UPDATE", { "property": updateProperty });
				}
				else {
					throw new Error("Trying to update a value that does not exist");
				}
			}
			else if (containerType === '@language') {
				const existingText = ldu.getObject().getText();
				const text = ldu.getObjectReplacement().getText();
				// Assuming target language is same as the replacing language
				const lang = ldu.getObjectReplacement().getLanguage();
				if (lang !== ldu.getObject().getLanguage()) throw new Error("Updates with a different language are not supported.");
				if (Array.isArray(updateNode[updateProperty][lang])) {
					const langItemIndex = updateNode[updateProperty][lang].indexOf(existingText);
					updateNode[updateProperty][lang][langItemIndex] = text;
				}
				else {
					updateNode[updateProperty][lang] = text;
				}
				processChangeEvents(boNode, "UPDATE", { "property": updateProperty });
			}
			else {
				let replacementText = ldu.getObjectReplacement().getText();
				if (updateNode[updateProperty]["@value"]) {
					updateNode[updateProperty]["@value"] = replacementText;
					// We replace the existing datatype with any new datatype if there is a replacing type
					if (replacementType) {
						updateNode[updateProperty][datatypeProp] = replacementType;
					}
					// Otherwise we delete the existing datatype
					else if (updateNode[updateProperty][datatypeProp]) {
						delete updateNode[updateProperty][datatypeProp];
					}
					// If there is a replacing language we use that
					// This will be of form {'@language': 'en'}
					if (ldu.getObjectReplacement().getLanguage()) {
						updateNode[updateProperty][languageProp] = ldu.getObjectReplacement().getLanguage();
					}
					else if (updateNode[updateProperty][languageProp]) {
						delete updateNode[updateProperty][languageProp];
					}
				}
				else if (valueProp !== 'undefined' && updateNode[updateProperty][valueProp]) {
					// Updating form where @value is aliased
					updateNode[updateProperty][valueProp] = replacementText;
					updateNode[updateProperty][datatypeProp] = datatype;
					if (language) {
						updateNode[updateProperty][languageProp] = ldu.getObjectReplacement().getLanguage();
					}
				}
				else {
					// This area is where most conflict will occur for functional property updates
					// In this instance it's a direct value on the property e.g. {myProp: 123}
					if (replacementType === integerXSDtype || replacementType === decimalXSDtype) {
						replacementText = Number(replacementText);
					}
					else if (replacementType === booleanXSDtype) {
						replacementText = replacementText === "true";
					}
					// So it's some other kind of replacing datatype so replace with object form
					else if (replacementType) {
						replacementText = {
							[valueProp]: replacementText,
							[datatypeProp]: replacementType
						};
					}
					// So it's some form of replacement including a language so replace with languag object form
					else if (ldu.getObjectReplacement().getLanguage()) {
						replacementText = {
							[valueProp]: replacementText,
							[languageProp]: ldu.getObjectReplacement().getLanguage()
						};
					}
					updateNode[updateProperty] = replacementText;
				}
				processChangeEvents(boNode, "UPDATE", { "property": updateProperty });
			}
		}
		else {
			if (containerType === '@set' || Array.isArray(updateNode[updateProperty])) {
				// Ensure property exists if not already there
				if (updateNode[updateProperty] == null) {
					updateNode[updateProperty] = [];
					processChangeEvents(boNode, "ADD-PROPERTY", { "property": updateProperty });
				}
				const idProp = internalContext._context.byIRI["@id"];
				let key = null;
				for (let i = 0; i < updateNode[updateProperty].length; i++) {
					const item = updateNode[updateProperty][i];
					// Array item is just an IRI
					if (typeof item === "string") {
						if (item === ldu.getObject().getText()) {
							key = i;
							break;
						}
					}
					// The @id or aliased @id object form
					else if (item[idProp] === ldu.getObject().getText()) {
						key = i;
						break;
					}
				}
				if (key !== null) {
					const updateThing = calcIRI(ldu.getObjectReplacement(), boNode, updateProperty);
					updateNode[updateProperty][key] = updateThing;
					processChangeEvents(boNode, "UPDATE", { "property": updateProperty });
				}
				else throw new Error("Cannot compute IRI to update in set");
			}
			else {
				// Here we need to consider whether property is @type of @id in which case we just set the value
				// But if not @type of @id then we need {"@id": IRI, "@type": "@id"} and also need to consider @id and @type aliases
				const updateThing = calcIRI(ldu.getObjectReplacement(), boNode, updateProperty);
				updateNode[updateProperty] = updateThing;
				processChangeEvents(boNode, "UPDATE", { "property": updateProperty });
			}
		}
	}

	function processReplace(ldr) {

		const iri = ldr.getSubject().getText();
		const graphIRI = ldr.getGraph().getText();
		const id = graphIRI + "___" + iri;
		const updateNode = getLoadedChannels()[id];
		if (!updateNode) throw new Error("Attempting to replace an unloaded resource: " + ldr.getSubject().getText());
		const boNode = getBoundObjects()[id];
		const isLoaded = checkIfPredicateLoaded(boNode, ldr.getPredicate().getText());
		if (!isLoaded) return;

		let ldd = patcher.createLDdelete();
		ldd.setSubject(ldr.getSubject());
		ldd.setPredicate(ldr.getPredicate());
		ldd.setObject(ldr.getObject());
		ldd.setGraph(ldr.getGraph());

		let lda = patcher.createLDadd();
		lda.setSubject(ldr.getSubject());
		lda.setPredicate(ldr.getPredicateReplacement());
		lda.setObject(ldr.getObjectReplacement());
		lda.setGraph(ldr.getGraph());

		processDelete(ldd);
		processAdd(lda);
	}

	function processUpdateList(ldu) {

		const iri = ldu.getSubject().getText();
		const predicateIRI = ldu.getPredicate().getText();
		const graphIRI = ldu.getGraph().getText();
		const id = graphIRI + "___" + iri;
		const updateNode = getLoadedChannels()[id];
		if (!updateNode) throw new Error("Attempting to update an unloaded resource: " + ldu.getSubject().getText());
		const boNode = getBoundObjects()[id];
		const isLoaded = checkIfPredicateLoaded(boNode, ldu.getPredicate().getText());
		if (!isLoaded) return;

		// We're assuming here can't add a list property on ontology manager
		const updateProperty = internalContext._context.byIRI[predicateIRI];
		let sliceBegin = parseInt(ldu.getStartIndex());
		let sliceEnd = parseInt(ldu.getEndIndex());

		if (sliceBegin === -1 && sliceEnd === -1) {
			if (!updateNode.hasOwnProperty(updateProperty)) {
				sliceBegin = 0;
				sliceEnd = 0;
			} else {
				sliceBegin = updateNode[updateProperty].length;
				sliceEnd = updateNode[updateProperty].length;
			}
		}
		// No start given so set to zero for processing
		if (sliceBegin === -1) sliceBegin = 0;
		// No end given so just use the list length
		if (sliceEnd === -1) {
			if (!updateNode.hasOwnProperty(updateProperty)) {
				sliceEnd = 0;
			} else {
				sliceEnd = updateNode[updateProperty].length;
			}
		}

		const spliceSize = sliceEnd - sliceBegin;

		const replacementText = ldu.getReplacementList().map(item => {
			if (item.constructor === Literal) {
				return calcLiteral(item, updateProperty);
			} else {
				return item.getText();
			}
		});

		Array.prototype.splice.apply(updateNode[updateProperty], [sliceBegin, spliceSize].concat(replacementText));
		processChangeEvents(boNode, "LIST", { "property": updateProperty });
	}

	// In certain circumstances there may not be enough information from the JSON-LD context to decide datatypes
	// In that instance the JSON-LD native types might be used and therefore when updating we need to look at the type
	// of the actual data to see if the updated value should automatically assume the same
	// In future we might allow the client to suggest an updated datatype too
	/*function updateLiteral(updateNode, boNode, updateProperty, updateValue) {
		const language = internalContext._context.propertyLanguage[updateProperty];
		const datatypeText = internalContext._context.propertyType[updateProperty];
		// If no language or datatype defined we need to try and work it out
		if (!language && !datatypeText) {
			// We have no context information to go on so we update according to existing datatype if makes sense
			if (typeof updateNode[updateProperty] === 'number') {
				updateNode[updateProperty] = Number(updateValue);
			}
			else {
				updateNode[updateProperty] = updateValue;
			}
		}
		else {
			// A datatype is defined so we don't need to worry about JSON native types
			updateNode[updateProperty] = updateValue;
		}
	}*/

	// Depending upon the context literals may be output as a string or as an object or as a native datatype
	// We calculate that here and return something that can be 'slotted' in as appropriate
	function calcLiteral(lo, updateProperty) {
		let newArrayItem = {};
		const text = lo.getText();
		const lang = lo.getLanguage();
		const datatype = lo.getDatatype();
		const language = internalContext._context.propertyLanguage[updateProperty];
		const valueProp = internalContext._context.byIRI['@value'];
		const languageProp = internalContext._context.byIRI['@language'];
		const datatypeText = internalContext._context.propertyType[updateProperty];
		const datatypeProp = internalContext._context.byIRI['@type'];

		if (language && language !== undefined) {
			// We have a datatype declared on the property in the context so we need to operate accordingly
			// If the local language is different then we need to output an object otherwise a string
			if (lang === language) {
				if (datatype === datatypeText) {
					// Even though it is declared locally it is the same as the
					// context language for the property so no need to output
					// Similarly for the datatype
					return text;
				}
			}
		}
		if (datatypeText && datatypeText !== undefined) {
			if (datatype === datatypeText) {
				// We have a datatype declared on the property in the context so
				// need to output datatype - just the literal value
				if (lang === null) {
					// So no local language means we can just output a string or native type
					let literalText = text;
					if (datatype === integerXSDtype || datatype === decimalXSDtype) {
						literalText = Number(text);
					}
					else if (datatype === booleanXSDtype) {
						literalText = text === "true";
					}
					return literalText;
				}
			}
		}
		else if (datatype === "http://www.w3.org/2001/XMLSchema#string") {
			return text;
		}
		else if (datatype === "http://www.w3.org/2001/XMLSchema#boolean") {
			return text === "true";
		}
		// else if (datatype === "http://www.w3.org/2001/XMLSchema#integer") {
		//	return Number(text);
		// }
		else if (!lang && !datatype) {
			// No language and not datatype so by default it is a string
			return text;
		}
		// Else we need to return an array
		newArrayItem[valueProp] = text;
		if (lang) newArrayItem[languageProp] = lang;
		// If the datatype is specified in the add and is different to
		// the context we need to add that in too
		if (datatype && datatype !== datatypeText) {
			newArrayItem[datatypeProp] = datatype;
		}
		return newArrayItem;
	}

	// Depending upon the context IRIs may be output as a string or as an object
	// e.g. ["http://example.com"] or [{"@id": "http://example.com"}]
	// We calculate that here and return something that can be 'slotted' in as appropriate
	function calcIRI(lo, bo, updateProperty) {

		let text = lo.getText();
		const idProp = internalContext._context.byIRI['@id'];
		const propertyType = internalContext._context.propertyType[updateProperty];

		// If it is type of @id then values will be strings for IRIs
		if (updateProperty === "type" && internalContext._context.byIRI[text]) {
			text = internalContext._context.byIRI[text];
		}

		if (propertyType === "@id") return text;
		// Else we need to return an array because they will be objects, e.g. {"@id": "IRI"}
		let newArrayItem = {};
		newArrayItem[idProp] = text;
		return newArrayItem;
	}

	this.parse = function (objects, message, isEntailed = false) {
		sessionID = message && message.session ? message.session : null;
		objects.forEach(o => {
			if (o.constructor === LDadd) {
				processAdd(o, isEntailed);
			} else if (o.constructor === LDdelete) {
				processDelete(o);
			} else if (o.constructor === LDreplace) {
				processReplace(o);
			} else if (o.constructor === LDupdate) {
				processUpdate(o);
			} else if (o.constructor === LDupdateList) {
				processUpdateList(o);
			} else if (o.constructor === LDinsertResource) {
				processInsertResource(o, message);
			} else if (o.constructor === LDdeleteResource) {
				processDeleteResource(o);
			} else if (o.constructor === LDreplaceResource) {
				processReplaceResource(o, message);
			}
		});
	};

	function processChangeEvents(node, eventType, eventData) {
		if (node._changeEvents) {
			node._changeEvents.forEach((callback) => {
				// We pass the session ID because an application may not want to process its own events
				// A single user may of course have several sessions open so we use sessions rather than user IDs
				callback(eventType, eventData, sessionID, node._iri);
			});
		}
	}

}

export default LdPatchProcessor;